/**
 * Seeds the legacy `structure` Redux slice (extra-field visibility, schedule
 * display mode) from the current user's saved site-detail view preferences.
 *
 * StructureCard and StructureDetailsList read those slice values directly
 * (not the preference itself), so this bridge has to run whenever the site
 * dashboard is mounted — not only while StructureCardConfigureDialog happens
 * to be open, which previously left the card showing defaults until the user
 * opened the configure dialog once per session.
 *
 * Contains:
 * - PREFERENCE_KEY / SCHEDULE_VIEW_KEY / VALID_SCHEDULE_VIEWS — shared with
 *   StructureCardConfigureDialog, which still owns the read/write UI for
 *   these same preferences
 * - useSiteViewPreferences — mount-effect hook (default export)
 */
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  setExtraStructureBoxFields,
  setScheduleView,
} from '@sophocles/features/sites/slices/selectedStructure/actions';
import useUserPreference from '@sophocles/hooks/useUserPreference';

export const PREFERENCE_KEY = 'view.siteDetail.fieldVisibility';
export const SCHEDULE_VIEW_KEY = 'view.siteDetail.scheduleView';
export const VALID_SCHEDULE_VIEWS = ['list', 'timeline'];

/**
 * Reads the two site-detail view preferences and dispatches them into the
 * `structure` slice once each has loaded. Guarded on the preference's own
 * `loaded` flag (same guard StructureCardConfigureDialog uses) so a slow
 * fetch can't overwrite the slice with a stale default.
 */
export default function useSiteViewPreferences() {
  const dispatch = useDispatch();

  const [savedFieldVisibility, , fieldVisibilityLoaded] = useUserPreference(PREFERENCE_KEY, []);
  const [savedScheduleView, , scheduleViewLoaded] = useUserPreference(SCHEDULE_VIEW_KEY, 'list');

  const savedFields = useMemo(
    () => (Array.isArray(savedFieldVisibility) ? savedFieldVisibility : []),
    [savedFieldVisibility],
  );
  const savedScheduleValue = VALID_SCHEDULE_VIEWS.includes(savedScheduleView) ? savedScheduleView : 'list';

  useEffect(() => {
    if (fieldVisibilityLoaded) {
      dispatch(setExtraStructureBoxFields(savedFields));
    }
  }, [fieldVisibilityLoaded, savedFields, dispatch]);

  useEffect(() => {
    if (scheduleViewLoaded) {
      dispatch(setScheduleView(savedScheduleValue));
    }
  }, [scheduleViewLoaded, savedScheduleValue, dispatch]);
}
